import { motion } from "framer-motion";
import { Check, Crown, Sparkles } from "lucide-react";
import { BRAND } from "../lib/constants";

const PACKAGES = [
    {
        name: "Basic Construction",
        price: "1,550",
        unit: "/sqft",
        tagline: "Solid foundations, honest pricing",
        features: [
            "Quality construction materials",
            "Standard interior finish",
            "Complete structural work",
            "Electrical & plumbing setup",
            "Daily site update reports",
            "On-time milestone delivery",
        ],
        featured: false,
    },
    {
        name: "Luxury Construction",
        price: "2,400",
        unit: "/sqft",
        tagline: "Premium-grade homes for life",
        features: [
            "Premium imported materials",
            "Designer luxury finishes",
            "Modular factory interiors",
            "Custom designer elevations",
            "Smart-home ready planning",
            "Vastu-aligned layouts",
            "Premium project support",
            "Housewarming setup included",
        ],
        featured: true,
    },
];

export default function Packages() {
    return (
        <section id="packages" className="relative bg-brand-bone py-20 md:py-32" data-testid="packages-section">
            <div className="max-w-7xl mx-auto px-5 md:px-10">
                <div className="text-center max-w-2xl mx-auto mb-14">
                    <div className="inline-flex items-center gap-3 mb-5">
                        <span className="w-8 h-px bg-brand-orange" />
                        <span className="text-[10px] uppercase tracking-[0.25em] text-brand-orange font-medium">Construction Packages</span>
                        <span className="w-8 h-px bg-brand-orange" />
                    </div>
                    <motion.h2
                        initial={{ opacity: 0, y: 16 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5 }}
                        className="font-display font-semibold text-brand-ink text-3xl md:text-4xl lg:text-[44px] leading-[1.05] tracking-tight"
                        data-testid="packages-heading"
                    >
                        Transparent pricing.{" "}
                        <span className="font-serif-italic font-normal brand-gradient-text">No surprises.</span>
                    </motion.h2>
                    <p className="mt-4 text-brand-muted text-[15px]">
                        Two clear packages. Itemized BoQ. Lifetime warranty on structure.
                    </p>
                </div>

                <div className="grid md:grid-cols-2 gap-5 md:gap-6 max-w-5xl mx-auto items-stretch">
                    {PACKAGES.map((p, i) => (
                        <motion.div
                            key={p.name}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-50px" }}
                            transition={{ duration: 0.5, delay: i * 0.1 }}
                            className={`relative rounded-3xl p-7 md:p-9 flex flex-col ${
                                p.featured
                                    ? "bg-brand-ink text-white overflow-hidden"
                                    : "bg-white border border-brand-line"
                            }`}
                            data-testid={`package-${i}`}
                        >
                            {p.featured && (
                                <>
                                    <div className="absolute -top-32 -right-24 w-80 h-80 rounded-full brand-gradient opacity-50 blur-3xl pointer-events-none" />
                                    <div className="absolute top-5 right-5 inline-flex items-center gap-1.5 brand-gradient text-white text-[10px] font-semibold uppercase tracking-[0.2em] px-3 py-1.5 rounded-full">
                                        <Sparkles size={11} /> Popular
                                    </div>
                                </>
                            )}

                            <div className="relative flex flex-col h-full">
                                <div className={`inline-flex items-center gap-2 ${p.featured ? "text-[#FF8800]" : "text-brand-muted"}`}>
                                    {p.featured ? <Crown size={14} /> : <span className="w-1.5 h-1.5 rounded-full bg-brand-ink" />}
                                    <span className="text-[10px] uppercase tracking-[0.2em]">{p.tagline}</span>
                                </div>

                                <h3 className={`mt-3 font-display font-semibold text-2xl md:text-3xl tracking-tight ${p.featured ? "text-white" : "text-brand-ink"}`}>
                                    {p.name}
                                </h3>

                                <div className="mt-5 flex items-baseline gap-1">
                                    <span className={`text-lg ${p.featured ? "text-white/70" : "text-brand-muted"}`}>₹</span>
                                    <span className={`font-display font-bold text-5xl md:text-6xl tracking-tighter leading-none ${p.featured ? "text-white" : "text-brand-ink"}`}>
                                        {p.price}
                                    </span>
                                    <span className={`text-sm ${p.featured ? "text-white/60" : "text-brand-muted"}`}>{p.unit}</span>
                                </div>

                                <div className={`my-6 h-px ${p.featured ? "bg-white/15" : "bg-brand-line"}`} />

                                <ul className="space-y-3 flex-1">
                                    {p.features.map((f) => (
                                        <li key={f} className="flex items-start gap-3">
                                            <span
                                                className={`mt-0.5 w-5 h-5 rounded-full flex items-center justify-center shrink-0 ${
                                                    p.featured ? "brand-gradient" : "bg-brand-bone"
                                                }`}
                                            >
                                                <Check size={11} className={p.featured ? "text-white" : "text-brand-ink"} strokeWidth={3} />
                                            </span>
                                            <span className={`text-[13px] md:text-sm ${p.featured ? "text-white/85" : "text-brand-ink/80"}`}>{f}</span>
                                        </li>
                                    ))}
                                </ul>

                                <a
                                    href={BRAND.whatsapp}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={`mt-7 flex items-center justify-center gap-2 w-full py-3.5 rounded-full font-medium text-sm transition ${
                                        p.featured
                                            ? "brand-gradient text-white hover:opacity-95"
                                            : "bg-brand-ink text-white hover:bg-brand-orange"
                                    }`}
                                    data-testid={`package-cta-${i}`}
                                >
                                    Choose this package
                                </a>
                            </div>
                        </motion.div>
                    ))}
                </div>

                <p className="mt-10 text-center text-brand-muted text-[13px]">
                    All-inclusive rates for <strong className="text-brand-ink font-semibold">home construction in Udaipur</strong>. Custom quotes available for plots above 3000 sqft.
                </p>
            </div>
        </section>
    );
}
